(function () {
  const provider = document.querySelector('bold-sc-adapter');
  if (!provider) return;

  const api = (provider.facade = provider.facade || { options: {}, pre: {}, state: {}, actions: {}, tools: {}, helpers: {} });
  

  // 1. CENTRALIZED EVENT NAMES (Legacy/Granular for compatibility)
  const EVENTS = {
      PRODUCT_HYDRATED: 'hub:product:hydrated',
      EXTERNAL_PRICING_LOADED: 'hub:external:pricing_loaded',
      OPTIONS_NORMALIZED: 'hub:form:options_normalized',
      QTY_TOTAL_UPDATED: 'hub:qty:total_updated', 
      MATRIX_QTY_UPDATED: 'hub:matrix:quantity_updated',
      OPTION_SELECTION_CHANGED: 'hub:option:selection_changed',
      PRICE_RECALCULATED: 'hub:price:recalculated', 
      STATE_CHANGE_GENERAL: 'hub:state:change', 
      VALIDATION_FAILED: 'hub:form:validation_failed',
      LOG_NEW_ENTRY: 'hub:log:new_entry',
      EVENT_DISPATCHED: 'hub:event:dispatched',
      CACHE_HIT: 'hub:debug:cache_hit',
      QUANTITY_BREAKS_LOADED: 'hub:pre:breaks_loaded',
      QUANTITY_PICKER_LOADED: 'hub:qty:picker_loaded',
      UI_REFLOW_NEEDED: 'hub:ui:reflow_needed',
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 2. THE STORE
  // ────────────────────────────────────────────────────────────────────────────
  const _store = {
    product: null, variant: null, originalPrice: 0, currentPrice: 0,    
    sizeExtras: 0, optionExtras: 0, optionOriginalExtras: 0, configuredPrice: 0, extras: 0,          
    totalPrice: 0, grandTotal: 0, quantity: 0, selections: {},
    sizeBreakdown: [], optionBreakdown: [], isMixedBag: false,
    conditionalOptions: [], tiers: [], matrix: {}, history: [],
    // Nudge & Active State 
    nextTierGap: 0, nextTierPrice: 0, activeIndex: -1,
    hasNormalized: false,
    // Internal Tracking
    _hiddenOptionTargets: new Set(),
    // ** RESURRECTION CACHE **
    _conditionalValueCache: {},
    // minimums
    minimumQuantity: 1,
    // ** FILE FLAGS (boolean per label, e.g. "Upload Artwork") **
    fileUploads: {}
  };

  // In-Memory Cache 
  const _memoCache = new Map();

    // ────────────────────────────────────────────────────────────────────────────
    // 2a. MEMORY STATUS (PER VARIANT) + POLLERS
    // ────────────────────────────────────────────────────────────────────────────
    // Tracks whether we've definitively seen qty_breaks for a given variant
    // 'unknown' | 'hasBreaks' | 'noBreaks'
    const _memoryStatusByVariant = Object.create(null);
    // Tracks active polling timers per variant so we don't duplicate intervals
    const _variantPollers = Object.create(null);

    function _hasPreApi() {
        return !!(
        window.BOLD &&
        window.BOLD.pre &&
        typeof window.BOLD.pre.getProductByVariantId === 'function'
        );
    }

  // 3. OPTIMIZED GETTERS
  api.state.getSnapshot = () => JSON.parse(JSON.stringify(_store));
  api.state.getHistory = () => _store.history;
  api.state.getSelection = (key) => key ? _store.selections[key] : _store.selections;
  api.state.getProduct = () => _store.product;
  api.state.getPrice = () => _store.totalPrice;
  api.state.getSizeExtras = () => _store.sizeExtras;
  api.state.getOptionExtras = () => _store.optionExtras;
  api.state.getOptionOriginalExtras = () => _store.optionOriginalExtras;
  api.state.getConfiguredPrice = () => _store.configuredPrice;
  api.state.getSizeBreakdown = () => _store.sizeBreakdown;
  api.state.getOptionBreakdown = () => _store.optionBreakdown;
  api.state.getFormattedTotal = () => api.tools.formatMoney(_store.totalPrice);
  api.state.getFormattedGrandTotal = () => api.tools.formatMoney(_store.grandTotal);
  api.state.getMatrix = () => JSON.parse(JSON.stringify(_store.matrix || {}));
  api.state.isMixedBag = () => _store.isMixedBag;
  api.state.isSpPricingActive = () => !!_store.externalPricing;
  api.state.getTiers = () => _store.tiers || [];
  api.state.getQuantity = () => _store.quantity;
  api.state.getActiveIndex = () => _store.activeIndex;
  api.state.getNextTierGap = () => _store.nextTierGap;
  api.state.nextTierPrice = () => _store.nextTierPrice;
  api.state.getCurrentPrice = () => _store.currentPrice;
  api.state.getMinimumQuantity = () => _store.minimumQuantity;

  // Quantity-break memory status: 'unknown' | 'hasBreaks' | 'noBreaks'
  // Defaults to the current _store.variant if no variantId is provided.
  api.state.getBreakStatus = (variantId) => {
    const id = variantId != null ? variantId : _store.variant;
    if (!id) return 'unknown';
    return _memoryStatusByVariant[id] || 'unknown';
  };

  // Convenience wrapper for UI components.
  // Returns true / false / 'unknown' depending on break status.
  api.state.hasQuantityBreaks = (variantId) => {
    const status = api.state.getBreakStatus(variantId);
    if (status === 'hasBreaks') return true;
    if (status === 'noBreaks') return false;
    return 'unknown';
  };


  api.tools.formatMoney = function(cents) {
    if (cents == null) return '';
    const format = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: format }).format(cents / 100);
  };

  // ────────────────────────────────────────────────────────────────────────────
  // DEBUG HELPERS (READ-ONLY – SAFE TO LEAVE IN)
  // ────────────────────────────────────────────────────────────────────────────
  api.debug = api.debug || {};

  api.debug.getConditionalSummary = function() {
    const selections = { ...( _store.selections || {} ) };
    const cache = { ...( _store._conditionalValueCache || {} ) };

    // Basic visibility map for .bold_option blocks in the DOM
    const domBlocks = Array.from(document.querySelectorAll('.bold_option')).map((blk) => {
      const titleEl = blk.querySelector('.bold_option_title, label, .form__label');
      const label = titleEl ? api.helpers.getSafeText(titleEl) : '(no title)';
      return {
        label,
        hiddenClass: blk.classList.contains('bold_option_hidden'),
        hasInitFlag: blk.classList.contains('bold-facade-init')
      };
    });

    return {
      selectionsKeys: Object.keys(selections),
      cacheKeys: Object.keys(cache),
      selections,
      cache,
      conditionalOptionRawNames: Array.isArray(_store.conditionalOptions)
        ? [..._store.conditionalOptions]
        : [],
      hiddenTargetsCount: _store._hiddenOptionTargets ? _store._hiddenOptionTargets.size : 0,
      domBlocks
    };
  };

  /**
   * Convenience logger – prints a grouped summary in the console and returns it.
   */
  api.debug.logConditionalSummary = function(label = 'Bold Facade – Conditional Snapshot') {
    const snap = api.debug.getConditionalSummary();
    console.groupCollapsed(label);
    console.log('Selections keys:', snap.selectionsKeys);
    console.log('Cache keys:', snap.cacheKeys);
    console.log('Selections:', snap.selections);
    console.log('Cache:', snap.cache);
    console.log('Conditional option rawNames:', snap.conditionalOptionRawNames);
    console.log('Hidden targets count:', snap.hiddenTargetsCount);
    console.table(
      snap.domBlocks.map((b) => ({
        label: b.label,
        hiddenClass: b.hiddenClass,
        hasInitFlag: b.hasInitFlag
      }))
    );
    console.groupEnd();
    return snap;
  };
  
  // HELPER: INTERNAL DISPATCHER FUNCTION
  function _internalDispatch(eventNameKey, detail = {}) {
      const eventName = EVENTS[eventNameKey];
      if (!eventName) {
          console.warn(`[Bold Facade] Attempted to dispatch unknown event key: ${eventNameKey}`);
          return;
      }
      document.dispatchEvent(new CustomEvent(eventName, { 
          detail, 
          bubbles: true, 
          composed: true 
      }));
      
      if (eventName !== EVENTS.EVENT_DISPATCHED) {
         document.dispatchEvent(new CustomEvent(EVENTS.EVENT_DISPATCHED, { 
             detail: { eventName: eventName, detail: detail }, 
             bubbles: true, 
             composed: true 
         }));
      }
  }

  // 4. UNIFIED DISPATCH API
  api.tools.dispatch = function(eventNameKey, detail) {
      _internalDispatch(eventNameKey, detail);
  };

    /**
   * Short-lived, per-variant polling for qty_breaks in BOLD.pre memory.
   * - Only runs while the given variant is active.
   * - Stops when breaks appear or a small timeout is reached.
   */
  function _watchMemoryForVariant(variantId) {
    if (!variantId) return;

    // If PRE isn't available yet, wait for it rather than silently returning.
    if (!_hasPreApi()) {
      const preWait = setInterval(() => {
        if (_hasPreApi()) {
          clearInterval(preWait);
          _watchMemoryForVariant(variantId);
        }
      }, 250);
      // Give up after 8 seconds — PRE isn't coming
      setTimeout(() => clearInterval(preWait), 8000);
      return;
    }

    const status = _memoryStatusByVariant[variantId] || 'unknown';
    if (status !== 'unknown') return; // already decided for this variant

    // Avoid double timers for the same variant
    if (_variantPollers[variantId]) return;

    const timeoutMs = 2500;
    const intervalMs = 150;
    const startedAt = Date.now();

    const timer = setInterval(() => {
      // Variant changed while we were polling → bail out
      if (_store.variant !== variantId) {
        clearInterval(timer);
        delete _variantPollers[variantId];
        return;
      }

      if (!_hasPreApi()) {
        clearInterval(timer);
        delete _variantPollers[variantId];
        return;
      }

      const productData = window.BOLD.pre.getProductByVariantId(variantId);
      const preVariant =
        productData && Array.isArray(productData.variants)
          ? productData.variants.find((v) => v.id == variantId)
          : null;

      const breaks = preVariant && preVariant.qty_breaks;

      if (Array.isArray(breaks) && breaks.length > 0) {
        clearInterval(timer);
        delete _variantPollers[variantId];
        _memoryStatusByVariant[variantId] = 'hasBreaks';

        // Re-run our normal pipeline so tiers + pricing update and events fire.
        _recalculateTotals();
        return;
      }

      if (Date.now() - startedAt > timeoutMs) {
        clearInterval(timer);
        delete _variantPollers[variantId];
        _memoryStatusByVariant[variantId] = 'noBreaks';
      }
    }, intervalMs);

    _variantPollers[variantId] = timer;
  }

  
  function _generateCalculationKey() {
      const matrixKeys = _store.matrix ? Object.keys(_store.matrix) : [];
      const isMatrixActive = matrixKeys.length > 0;
      const filteredSelections = {};
      if (_store.selections) {
          for (const [key, value] of Object.entries(_store.selections)) {
              const cleanKey = key.replace(/\*/g, '').trim().toLowerCase();
              const isMatrixControlled = matrixKeys.some(mk => 
                  mk.replace(/\*/g, '').trim().toLowerCase() === cleanKey
              );
              if (!isMatrixControlled) filteredSelections[key] = value;
          }
      }
      const keyInputs = {
          matrix: _store.matrix,
          selections: filteredSelections,
          originalPrice: _store.originalPrice,
          variant: _store.variant
      };
      if (!isMatrixActive) keyInputs.qty = _store.quantity;
      return JSON.stringify(keyInputs);
  }

  function cleanName(str) { return String(str || '').replace(/\*/g, '').trim().toLowerCase(); }
  
  function cleanValue(str) { 
    if (!str) return '';

    return String(str)
        // 1. Convert HTML entities back to characters 
        // (This fixes cases where " is &quot;)
        .replace(/&quot;/g, '"')
        
        // 2. Strict Price Removal: Only remove () if they contain + or $
        // We use [^)]* to be non-greedy and stop at the first closing bracket
        .replace(/\s*\(\s*[\+\$][^)]*\)/g, '') 
        
        // 3. Remove trailing prices like " +$5.00" or " +5.00"
        .replace(/\s*\+\s*\$?\d+(\.\d{2})?\s*$/, '') 
        
        // 4. Remove brackets like [In Stock]
        .replace(/\s*\[.*?\]/g, '') 
        
        // 5. CRITICAL: If the string was cut off by an unescaped quote in HTML
        // (e.g. "7 1/4"), ensure we don't have dangling start-parentheses
        .trim() 
        .toLowerCase(); 
  }

  function _resetOptionState(container) { 
    container.classList.remove('bold-facade-init'); 
    _store._hiddenOptionTargets.add(container); 
  }

  // NEW: targeted resurrection for a single option container when it becomes visible again

// Tracks the MutationObserver instance for class changes on .bold_option blocks
let _resetObserver = null;

/**
 * Targeted resurrection for a single .bold_option container when it becomes visible.
 * - Re-applies first-time init for selects (label price stripping).
 * - Resolves the same label key used by handleInteraction (name-based, then title).
 * - Looks up the cached value in _conditionalValueCache (raw + canonical key).
 * - If current selection differs from cache, uses changeOption to restore it.
 */
// FILE: assets/bold-facade.js

function _resurrectVisibleOption(container) {
  if (!container || container.classList.contains('bold_option_hidden')) return;

  const wasHidden = _store._hiddenOptionTargets && _store._hiddenOptionTargets.has(container);
  const isInitialized = container.classList.contains('bold-facade-init');

  // 1. First-time setup for selects (label price stripping)
  if (!isInitialized) {
    const inputEl = container.querySelector('select, input, textarea');
    if (inputEl && inputEl.tagName === 'SELECT') {
      _updateOptionLabelsFromElement(inputEl);
    }
  }

  // 2. Resolve label (prefer name, fall back to title)
  const optionTitleEl = container.querySelector('.bold_option_title, label, .form__label');
  let label = null;

  const primaryInput = container.querySelector('select[name], input[name], textarea[name]');
  if (primaryInput && primaryInput.name) {
    const m = primaryInput.name.match(/properties\[(.*?)\]/);
    if (m && m[1]) {
      label = m[1];
    }
  }

  if (!label && optionTitleEl) {
    label = api.helpers.getSafeText(optionTitleEl);
  }

  if (!label) {
    if (!isInitialized) container.classList.add('bold-facade-init');
    if (wasHidden && _store._hiddenOptionTargets) _store._hiddenOptionTargets.delete(container);
    return;
  }

  // 3. NEW: PRE-SELECTED SCAN
  // Check if Bold has pre-selected an option in this newly visible group
  // This ensures conditional defaults (like thread color) show up in Price Display immediately.
  const activeInput = container.querySelector('input:checked, option:checked');
  if (activeInput && activeInput.value && activeInput.value !== 'Unchecked') {
      let preselectedImg = null;

      // Capture the image URL if Bold has a swatch span for this preselected input
      const swatchSpan = activeInput.closest('.bold_option_value')?.querySelector('.bold_option_value_swatch span');
      if (swatchSpan) {
          const bgImg = swatchSpan.style.backgroundImage;
          if (bgImg && bgImg !== 'none') {
              preselectedImg = bgImg.slice(4, -1).replace(/"/g, "");
          }
      }

      // Register both value AND image so the zoom/eye icon works immediately
      api.actions.updateOption(label, activeInput.value, preselectedImg);
      // console.log(`[Facade] 👻 Pre-selected conditional detected: ${label} = ${activeInput.value} (Img: ${preselectedImg ? 'Yes' : 'No'})`);
  }

  const canonicalLabel = typeof api.helpers.canonicalOptionKey === 'function' ? api.helpers.canonicalOptionKey(label) : label;

  // 4. Locate cached value using raw + canonical keys
  let cacheKey = null;
  if (_store._conditionalValueCache && Object.prototype.hasOwnProperty.call(_store._conditionalValueCache, label)) {
    cacheKey = label;
  } else if (_store._conditionalValueCache && canonicalLabel && Object.prototype.hasOwnProperty.call(_store._conditionalValueCache, canonicalLabel)) {
    cacheKey = canonicalLabel;
  }

  // 5. Targeted Resurrection
  if (cacheKey) {
    const cachedValue = _store._conditionalValueCache[cacheKey];
    const currentValue = (_store.selections && _store.selections[label] !== undefined
      ? _store.selections[label]
      : _store.selections && _store.selections[canonicalLabel] !== undefined
      ? _store.selections[canonicalLabel]
      : undefined);

    if (!_areValuesEqual(currentValue, cachedValue)) {
      try {
        api.options.changeOption(label, cachedValue, container);
      } catch (err) {
        console.warn('[Bold Facade] Resurrection failed for', label, err);
      }
    }
  }

  // 6. Force display refresh and finalize state
  try {
    _recalculateTotals();
  } catch (err) {
    console.warn('[Bold Facade] Resurrection forced recalc failed', err);
  }

  if (!isInitialized) container.classList.add('bold-facade-init');
  if (wasHidden && _store._hiddenOptionTargets) _store._hiddenOptionTargets.delete(container);
}

/**
 * Observes class changes on .bold_option blocks to:
 * - Reset init state when an initialized option becomes hidden.
 * - Trigger targeted resurrection when a hidden option becomes visible.
 */
function _setupResetObserver() {
  const form =
    document.querySelector('product-form') ||
    document.querySelector('form[action*="/cart/add"]');
  if (!form || _resetObserver) return;

  const config = {
    attributes: true,
    subtree: true,
    attributeFilter: ['class'],
    attributeOldValue: true
  };

  const callback = (mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') continue;

      const container = mutation.target;
      if (!container.classList || !container.classList.contains('bold_option')) continue;

      const oldClass = mutation.oldValue || '';
      const wasHidden = oldClass.indexOf('bold_option_hidden') !== -1;
      const isHidden = container.classList.contains('bold_option_hidden');

      // CASE 1: Initialized element was just hidden → reset its init flag & track as hidden
      if (isHidden && container.classList.contains('bold-facade-init')) {
        // console.log('[Facade Reset] Detected initialized element was hidden. Resetting state.');
        _resetOptionState(container);
        continue;
      }

      // CASE 2: Previously hidden element just became visible → targeted resurrection
      if (wasHidden && !isHidden) {
        // console.log('[Facade Reset] Detected conditional element reveal. Running targeted resurrection.');
        
        // Notify the Adapter to re-run grid enhancement (Show All button, etc.)
        api.tools.dispatch('UI_REFLOW_NEEDED', { reason: 'conditional_reveal' });

        // Defer to let Bold finish any internal DOM updates, then scan for preselected values
        requestAnimationFrame(() => _resurrectVisibleOption(container));
      }
    }
  };

  _resetObserver = new MutationObserver(callback);
  _resetObserver.observe(form, config);
}

  // Equality Helper for Arrays
  function _areValuesEqual(a, b) {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, index) => val === sortedB[index]);
    }
    return false;
  }

  // LOGGING FUNCTION
  function _pushLog(type, field, value) {
    const now = Date.now();
    const last = _store.history[_store.history.length - 1];
    if (last && last.field === field && String(last.value) === String(value) && (now - last._ts < 500)) return;
    const record = { time: new Date().toISOString().split('T')[1].slice(0,8), type, field, value, _ts: now };
    _store.history.push(record);
    if (_store.history.length > 100) _store.history.shift();
    
    api.tools.dispatch('STATE_CHANGE_GENERAL', { 
        action: 'log', 
        record, 
        state: api.state.getSnapshot(), 
        formattedTotal: api.state.getFormattedTotal() 
    });
    api.tools.dispatch('LOG_NEW_ENTRY', { record });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // 3. NORMALIZER & PRICING HELPERS
  // ────────────────────────────────────────────────────────────────────────────

  function _syncCheckboxWrapperClasses(container) {
  if (!container) return;

  // We run this for all checkbox options, but it's mainly to fix checkboxmulti.
  const checkboxes = container.querySelectorAll('input[type="checkbox"]');

  checkboxes.forEach((input) => {
    const wrapper = input.closest('.bold_option_value');
    if (!wrapper) return;

    // Just mirror the actual "checked" state into the CSS class.
    if (input.checked) {
      wrapper.classList.add('bold_checked');
    } else {
      wrapper.classList.remove('bold_checked');
    }
  });
}

    // --- DEFAULT SELECTION BOOTSTRAP -------------------------------------
  // Ensures Bold's native defaults / preselections are reflected in the hub store.
  let _defaultsApplied = false;

  function _bootstrapDefaultSelections() {
    // If we've already successfully run once, bail.
    if (_defaultsApplied) return;

    try {
      const opts = api.options.getOptionSets();
      // If options aren't hydrated yet, wait for a later pass.
      if (!Array.isArray(opts) || !opts.length) return;

      let didChange = false;

      opts.forEach((opt) => {
        const val = opt.value;

        const hasValue =
          val != null &&
          !(
            (typeof val === 'string' && val.trim() === '') ||
            (Array.isArray(val) && val.length === 0)
          );

        if (!hasValue) return;

        // Use the same kind of key that handleInteraction uses
        // (any label is fine because we normalize with cleanName later).
        const key = opt.prettyName || opt.name || opt.id;
        if (_store.selections[key] !== undefined) return;

        _store.selections[key] = val;

        // Keep the sticky conditional cache in sync so resurrection still works.
        const rawKey = String(key);
        const canonicalKey =
          typeof _canonicalOptionKey === 'function'
            ? _canonicalOptionKey(rawKey)
            : rawKey;

        _store._conditionalValueCache[rawKey] = val;
        if (canonicalKey && canonicalKey !== rawKey) {
          _store._conditionalValueCache[canonicalKey] = val;
        }

        didChange = true;
      });

      // Mark as done once we've had a real shot at options
      _defaultsApplied = true;

      // If we actually seeded anything, recompute totals with a fresh key.
      if (didChange) {
        _recalculateTotals(true);
      }
    } catch (err) {
      console.warn('[Bold Facade] Default selection bootstrap failed', err);
    }
  }

  // FILE: assets/bold-sc-adapter.js
// FILE: assets/bold-sc-adapter.js
function _isDropdownPlaceholderCleared(optionKey) {
  // Detects when a dropdown for the given label has been explicitly reset
  // to its blank/placeholder value.
  try {
    if (!optionKey) return false;

    const safeName = CSS.escape(`properties[${optionKey}]`);

    // Prefer a select with the matching name
    let selectEl = document.querySelector(`select[name="${safeName}"]`);

    // Fallback: look for a bold_option block with matching title, then its select
    if (!selectEl) {
      const blocks = document.querySelectorAll('.bold_option');
      for (const blk of blocks) {
        const titleEl = blk.querySelector('.bold_option_title, label, .form__label');
        if (!titleEl) continue;
        const label = api.helpers.getSafeText(titleEl); // existing helper
        if (label === optionKey) {
          const maybeSelect = blk.querySelector('select');
          if (maybeSelect) {
            selectEl = maybeSelect;
            break;
          }
        }
      }
    }

    if (!selectEl) return false;

    const value = selectEl.value;
    const selectedOption = selectEl.options[selectEl.selectedIndex] || null;
    const text = (selectedOption ? selectedOption.textContent : '').trim();

    // Primary signal: empty value (true placeholder)
    if (value === '' || value == null) {
      return true;
    }

    // Secondary: text *looks* like a placeholder (e.g. "-- Shirt Color")
    if (/^--\s*/.test(text)) {
      return true;
    }

    return false;
  } catch (e) {
    console.warn('[Bold Facade] _isDropdownPlaceholderCleared error', e);
    return false;
  }
}
// # WHY: Use the option label to locate the backing <select> and treat an empty
// value / placeholder text ("-- Shirt Color") as an intentional reset, while
// avoiding any effect on checkboxes or other control types.


// Initialization logic (Monolithic to avoid breaking legacy chains)
function _initializeOptionsFlow() { 
  const form = document.querySelector('product-form') || document.querySelector('form[action*="/cart/add"]');
  if (!form) return;

  const allOptionContainers = form.querySelectorAll('.bold_option');

  allOptionContainers.forEach(container => {
    const isHidden = container.classList.contains('bold_option_hidden');
    
    // Self-Healing: Fix stuck flags on hidden elements
    if (isHidden && container.classList.contains('bold-facade-init')) {
      container.classList.remove('bold-facade-init');
      _store._hiddenOptionTargets.add(container);
    }

    const isInitialized = container.classList.contains('bold-facade-init');

    // PART A: Handle Visible Options
    if (!isHidden) {
      
      // 1. STANDARD SETUP (Price Stripping)
      if (!isInitialized) {
        const inputEl = container.querySelector('select, input, textarea');
        if (inputEl && inputEl.tagName === 'SELECT') {
          _updateOptionLabelsFromElement(inputEl);
        }
        _syncCheckboxWrapperClasses(container);
      }
      
      // 2. RESURRECTION CHECK (Runs on every pass to ensure sync)
      // We check if the option is visible but the store doesn't match the cache
      const optionTitleEl = container.querySelector('.bold_option_title, label, .form__label');
      let label = null;

      // Prefer the same name-based key used by handleInteraction
      const primaryInput = container.querySelector('select[name], input[name], textarea[name]');
      if (primaryInput && primaryInput.name) {
        const m = primaryInput.name.match(/properties\[(.*?)\]/);
        if (m && m[1]) {
          label = m[1];
        }
      }

      // Fallback to visible title text if we couldn't get a name-based label
      if (!label && optionTitleEl) {
        label = api.helpers.getSafeText(optionTitleEl);
      }

      /*
      if (label && _store._conditionalValueCache[label]) {
        const cachedValue = _store._conditionalValueCache[label];
        const currentValue = _store.selections[label]; 

        // Only resurrect if the active state is different (and cache isn't empty)
        if (!_areValuesEqual(currentValue, cachedValue)) {
          // 1. Restore to DOM via Event (Triggers recalc)
          api.options.changeOption(label, cachedValue, container); 
        }
      }
*/
      if (label) {
        const canonicalLabel = typeof api.helpers.canonicalOptionKey === 'function' ? api.helpers.canonicalOptionKey(label) : label;

        // Try raw then canonical in cache
        let cacheKey = null;
        if (Object.prototype.hasOwnProperty.call(_store._conditionalValueCache, label)) {
          cacheKey = label;
        } else if (
          canonicalLabel &&
          Object.prototype.hasOwnProperty.call(_store._conditionalValueCache, canonicalLabel)
        ) {
          cacheKey = canonicalLabel;
        }

        if (cacheKey) {
          const cachedValue = _store._conditionalValueCache[cacheKey];

          // Check current selection under both raw & canonical keys
          const currentValue =
            _store.selections[label] !== undefined
              ? _store.selections[label]
              : _store.selections[canonicalLabel];

          // Skip resurrection if user is actively typing inside this container
          const activeEl = document.activeElement;
          if (activeEl && container.contains(activeEl)) {
            return;
          }

          // Only resurrect if the active state is different (and cache isn't empty)
          if (!_areValuesEqual(currentValue, cachedValue)) {
            api.options.changeOption(label, cachedValue, container);
          }
        }
      }
      // 3. Mark Initialized
      if (!isInitialized) {
        container.classList.add('bold-facade-init');
        if (_store._hiddenOptionTargets.has(container)) {
          _store._hiddenOptionTargets.delete(container);
        }
      }
    } 
    // PART B: Handle Hidden Options
    else if (isHidden && !isInitialized && !_store._hiddenOptionTargets.has(container)) { 
      _store._hiddenOptionTargets.add(container);
    }
  });

  // NEW: one-time sync for Bold defaults / preselections
  _bootstrapDefaultSelections();
}


function _stripOptionLabelPrice(str) {
    str = String(str || '');
    str = str.replace(/\s*\[\s*\+?\$\s*\d+\.\d{2}\s*\]/, '');
    str = str.replace(/\s*\[\s*\+?.*?\]/, ''); 
    str = str.replace(/\s*\(\s*[\+\$].*?\)/g, '');
    return str.trim();
}
  
  function _isSpPricingRequired() {
    const opts = api.options.getOptionSets(); 
    const triggerPhrase = 'ink color'; 
    const locationKeywords = ['front', 'back', 'sleeve'];
    return opts.some(opt => {
        const name = cleanName(opt.name);
        return name.includes(triggerPhrase) && locationKeywords.some(loc => name.includes(loc));
    });
  }

  function _processExternalPricingJSON() {
    const script = document.getElementById('sp-pricing-json');
    if (!script) return;
    try {
        const raw = JSON.parse(script.textContent);
        const processed = { breaks: raw.breaks, rates: {} };
        for (const [location, colorCounts] of Object.entries(raw.rates)) {
            processed.rates[location] = {};
            for (const [count, prices] of Object.entries(colorCounts)) {
                processed.rates[location][count] = prices.map(p => Math.round(p * 100));
            }
        }
        _store.externalPricing = processed;
        api.tools.dispatch('EXTERNAL_PRICING_LOADED', { pricing: processed }); 
    } catch (e) { console.error('[Bold Facade] JSON Load Error', e); }
  }

  function _mapOptionToLocation(rawName) {
    const name = cleanName(rawName);
    if (name.includes('front')) return 'front';
    if (name.includes('back')) return 'back';
    if (name.includes('sleeve')) return 'sleeves'; 
    return null;
  }

  function _extractColorCount(rawValue) {
    const match = String(rawValue).match(/(\d+)/);
    return match ? match[1] : '0';
  }

  function _getPricingTierIndex(qty) {
    const data = _store.externalPricing;
    if (!data || !data.breaks) return -1;
    let activeIndex = 0; 
    for (let i = 0; i < data.breaks.length; i++) {
        if (qty >= data.breaks[i]) activeIndex = i;
    }
    return activeIndex;
  }

  function _calcOptionPrice(location, colorCount) {
    const data = _store.externalPricing;
    if (!data || !data.rates[location] || !data.rates[location][colorCount]) {
      return { unitPrice: 0, previousUnitPrice: 0, didReceiveBreak: false };
    }

    const matrixTotal = _store.matrix
      ? Object.values(_store.matrix).reduce(
          (sum, g) => sum + Object.values(g).reduce((a, b) => a + b, 0),
          0
        )
      : 0;

    const qty = matrixTotal > 0 ? matrixTotal : _store.quantity;
    const tierIndex = _getPricingTierIndex(qty);

    if (tierIndex === -1) {
      return { unitPrice: 0, previousUnitPrice: 0, didReceiveBreak: false };
    }

    const priceList = data.rates[location][colorCount];
    const currentIndex = Math.min(tierIndex, priceList.length - 1);
    const unitPrice = priceList[currentIndex];

    let previousUnitPrice = unitPrice;
    let didReceiveBreak = false;

    // “Next break” = we’re at a higher tier than before, and price actually dropped
    if (currentIndex > 0) {
      previousUnitPrice = priceList[currentIndex - 1];
      didReceiveBreak = previousUnitPrice > unitPrice;
    }

    return { unitPrice, previousUnitPrice, didReceiveBreak };
  }


  /*
  function _calcOptionPrice(location, colorCount) {
    const data = _store.externalPricing;
    if (!data || !data.rates[location] || !data.rates[location][colorCount]) return 0;
    const matrixTotal = _store.matrix ? Object.values(_store.matrix).reduce((sum, g) => sum + Object.values(g).reduce((a,b)=>a+b,0), 0) : 0;
    const qty = matrixTotal > 0 ? matrixTotal : _store.quantity;
    const tierIndex = _getPricingTierIndex(qty);
    if (tierIndex === -1) return 0;
    const priceList = data.rates[location][colorCount];
    return priceList[Math.min(tierIndex, priceList.length - 1)];
  }
*/
  // MODIFIED: normalizeNativeOption 
function normalizeNativeOption(opt, idx, groupId = 0) {
    const fields = opt.fields || {}; 
    const valuesRawUnconverted = opt.options_values || 
                                 opt.optionValues || 
                                 opt.optionData?.optionValues || 
                                 [];
    const valuesRaw = Array.from(valuesRawUnconverted); 
    let val = null;
    if (opt.selectedValue) val = opt.selectedValue;
    else {
        const selectedItem = valuesRaw.find(v => v.selected);
        val = selectedItem ? (selectedItem.value || selectedItem.name) : null;
    }
    if ((opt.optionType === 'file' || opt.type === 'file') && opt.value) val = opt.value;
    const rawTitle = fields.optionTitle || opt.publicName || opt.internalName || `Option ${idx + 1}`;
    let displayTitle = rawTitle; 
    let type = opt.fields.optionType || opt.optionType || fields.type; 
    if (!type) { 
        type = valuesRaw.length ? 'dropdown' : 'input'; 
    }
    
    return {
      id: (opt.id || `opt-${idx}`).toString(),
      name: cleanName(displayTitle), 
      prettyName: displayTitle.replace(/\*/g, '').trim(),
      rawName: `properties[${rawTitle}]`,
      type: type,
      value: val,
      values: valuesRaw.map(v => v.value || v.name),
      valuesData: valuesRaw.map(v => ({ value: String(v.value || v.name).trim(), price: parseInt(v.price || 0, 10) })),
      groupId: String(groupId),
      required: !!opt.isRequired,
      isPriced: !!opt.isPricedOption,
      hasConditions: !!opt.conditions && Array.isArray(opt.conditions) && opt.conditions.length > 0,
      uniqueClass: fields.uniqueClassname || '' 
    };
}

  function normalizeDomOption(blk, i) {
      const titleEl = blk.querySelector('.bold_option_title');
      let title = api.helpers.getSafeText(titleEl) || `Option ${i}`; 
      const required = title.includes('*') || blk.classList.contains('bold_option_value_required');
      let val = '', type = 'input';
      const sel = blk.querySelector('select');
      const file = blk.querySelector('input[type="file"]');
      const check = blk.querySelector('input:checked');
      const text = blk.querySelector('input[type="text"], textarea');
      let valuesList = [], valuesDataList = [];

      if (sel) { 
          type='dropdown'; val=sel.value; 
          Array.from(sel.options).forEach(opt => {
              if (opt.value && !opt.value.startsWith('--')) {
                  const txt = opt.textContent;
                  let price = 0;
                  if (txt.includes('$')) { const match = txt.match(/\$\s?(\d+(\.\d{2})?)/); if (match) price = Math.round(parseFloat(match[1]) * 100); }
                  valuesList.push(opt.value);
                  valuesDataList.push({ value: opt.value, price: price });
              }
          });
      }
      else if (file) { type='file'; if(file.files.length) val=file.files[0].name; }
      else if (check) { type=check.type; val=check.value; }
      else if (text) { val=text.value; }
      return { id: `dom-${i}`, name: cleanName(title), prettyName: title.replace(/\*/g, '').trim(), rawName: `properties[${title}]`, type, value: val, values: valuesList, valuesData: valuesDataList, groupId: '0', required };
  }
  
  function _getOptionsFromDOM(root) {
    const scope = root || document;
    return Array.from(scope.querySelectorAll('.bold_option'))
        .filter(blk => !blk.classList.contains('bold_option_hidden'))
        .map((blk, i) => normalizeDomOption(blk, i));
  }
  
  function _isOptionConditional(opt) { return !!opt.conditions && Array.isArray(opt.conditions) && opt.conditions.length > 0; }

  function _getOptionsFromNative() {
    const product = window.BOLD.options.app.optionProducts[0];
    if (!product?.optionSets?.length) return []; 

    const flat = [];
    _store.conditionalOptions = []; 

    product.optionSets.forEach((set, sIdx) => {
        const rawOptions = set.options || set.groupOptions || set.allOptions || set.items || [];
        const optionList = Array.from(rawOptions);

        for (let oIdx = 0; oIdx < optionList.length; oIdx++) {
            const opt = optionList[oIdx];
            const clonedOpt = _safeDeepClone(opt);
            const item = normalizeNativeOption(clonedOpt, oIdx, sIdx);
            
            if (item.hasConditions) {
                _store.conditionalOptions.push(item.rawName);
            }
            if (item.type === 'file') {
                const domEl = document.querySelector(`[name="properties[${item.name}]"]`);
                if (domEl && domEl.type === 'file' && domEl.files.length) item.value = domEl.files[0].name;
            }
            flat.push(item);
        }
    });
    return flat;
  }

  function _safeDeepClone(obj) {
    if (typeof obj !== 'object' || obj === null) return obj;
    const replacer = () => {
        const cache = new WeakSet();
        return (key, value) => {
            if (typeof value === 'object' && value !== null) {
                if (value.nodeType || value.tagName) return `[DOM Element: ${value.tagName || 'Unknown'}]`;
                if (key === 'context' || key === 'app' || key === 'ee' || key === 'optionProduct') {
                    return '[Framework Reference]';
                }
                if (cache.has(value)) {
                    return '[Circular]';
                }
                cache.add(value);
            }
            return value;
        };
    };
    try { return JSON.parse(JSON.stringify(obj, replacer())); } catch (e) {
        console.warn("Deep clone failed due to unhandled circular structure:", e);
        return { ...obj };
    }
  }

  // Helper for Visibility Check
  function _isDomElementVisible(cleanOptionName) {
    const selector = `[name*="properties[${cleanOptionName.replace(/-/g, '\\-')}]"]`;
    const inputElement = document.querySelector(selector);
    const container = inputElement ? inputElement.closest('.bold_option') : 
        Array.from(document.querySelectorAll('.bold_option')).find(blk => 
            cleanName(blk.querySelector('.bold_option_title')?.textContent) === cleanOptionName
        );
    if (container) {
        return !container.classList.contains('bold_option_hidden');
    }
    return false; 
  }

  api.options.getOptionSets = function(root) {
    const caps = provider.getCapabilities ? provider.getCapabilities() : {};
    let options = [];
    let isNativeSource = false;

    if (caps.nativeOptions && window.BOLD.options.app.optionProducts.length > 0) {
        try {
            options = _getOptionsFromNative();
            isNativeSource = true;
        } catch (e) {
            console.warn("Native Options parsing failed, falling back to DOM scraping.", e);
            options = _getOptionsFromDOM(root);
        }
    } else {
        options = _getOptionsFromDOM(root);
    }
    
    // --- Unified Visibility Filter ---
    if (isNativeSource) {
        options = options.filter(opt => {
            const cleanKey = cleanName(opt.name);
            if (opt.hasConditions || _store.conditionalOptions.includes(opt.rawName)) {
                if (!_isDomElementVisible(cleanKey)) {
                    return false; // Filter it out of the visible list
                }
            }
            return true; 
        });
    }

    if (options.length > 0 && !_store.hasNormalized) {
        _store.hasNormalized = true;
        api.tools.dispatch('OPTIONS_NORMALIZED', { optionsCount: options.length });
    }

    return options;
  };
  
  api.options.validate = async function() {
      const caps = provider.getCapabilities ? provider.getCapabilities() : {};
      if (caps.nativeOptions && window.BOLD.options.app.optionProducts.length > 0) {
          try {
              await window.BOLD.options.app.optionProducts[0].validateOptions();
              return { valid: true };
          } catch (err) {
              api.tools.dispatch('VALIDATION_FAILED', { error: err.message || err });
              return { valid: false, error: err };
          }
      }
      const opts = api.options.getOptionSets();
      const invalid = opts.find(o => o.required && !o.value);
      if (invalid) {
          const errorMsg = `Please complete ${invalid.prettyName}`;
          api.tools.dispatch('VALIDATION_FAILED', { error: errorMsg, field: invalid.prettyName });
          return { valid: false, error: errorMsg };
      }
      return { valid: true };
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 4. DATA SYNC (Memory-Based)
  // ────────────────────────────────────────────────────────────────────────────

  function _syncFromMemory() {
      if (!_hasPreApi()) return false;

      const variantId = _store.variant;
      if (!variantId) return false;

      const productData = window.BOLD.pre.getProductByVariantId(variantId);
      if (!productData || !Array.isArray(productData.variants)) return false;

      const preVariant = productData.variants.find(v => v.id == variantId);

      if (preVariant && preVariant.qty_breaks) {
          const sortedBreaks = [...preVariant.qty_breaks].sort((a, b) => a.qty - b.qty);
          
          if (sortedBreaks.length > 0 && sortedBreaks[0].qty > 1) {
              sortedBreaks.unshift({ qty: 1, price: _store.originalPrice });
          } else if (sortedBreaks.length === 0) {
               sortedBreaks.push({ qty: 1, price: _store.originalPrice });
          }
          
                _store.tiers = sortedBreaks.map((qb, i) => {
                  const nextBreak = sortedBreaks[i + 1];
                  return {
                    min: parseInt(qb.qty, 10),
                    max: nextBreak ? (parseInt(nextBreak.qty, 10) - 1) : Number.MAX_SAFE_INTEGER,
                    price: parseInt(qb.price, 10),
                    index: i
                  };
                });

                // We have definitively seen breaks for this variant
                const prevStatus = _memoryStatusByVariant[variantId];
                _memoryStatusByVariant[variantId] = 'hasBreaks';

                // Only announce the transition the first time for this variant
                if (prevStatus !== 'hasBreaks') {
                  api.tools.dispatch('QUANTITY_BREAKS_LOADED', {
                    variantId,
                    tiers: _store.tiers.slice()
                  });
                }

                return true;
      }
      return false;
  }

  function _recalculateTotals(force = false) {
        // Guard: if variant not set yet, Bold hasn't finished initializing.
        // Schedule a retry rather than memoizing a partial-state result.
        if (!_store.variant) {
          setTimeout(() => _recalculateTotals(force), 200);
          return;
        }

        const hasMemoryTiers = _syncFromMemory();
        const key = _generateCalculationKey();

        if (!force && _memoCache.has(key)) {
            const cached = _memoCache.get(key);
            
            // ** CRITICAL FIX: Protect non-calculation state from being overwritten by cache **
            const hiddenTargetsRef = _store._hiddenOptionTargets;
            const valueCacheRef = _store._conditionalValueCache; 
            
            Object.assign(_store, cached);
            
            _store._hiddenOptionTargets = hiddenTargetsRef; 
            _store._conditionalValueCache = valueCacheRef;       

            api.tools.dispatch('PRICE_RECALCULATED', { isMemoized: true, grandTotal: _store.grandTotal });
            api.tools.dispatch('CACHE_HIT', { key }); 
            return;
        }

      // --- PRUNING LOGIC (Updated with DOM Flag Reset) ---
      const opts = api.options.getOptionSets(); 
      const visibleOptionKeys = new Set(opts.map(o => cleanName(o.name)));

      for (const key in _store.selections) {
          if (Object.prototype.hasOwnProperty.call(_store.selections, key)) {
              const cleanKey = cleanName(key);
              
              if (!visibleOptionKeys.has(cleanKey)) {
                  // 1. Delete from active state (Pruning)
                  delete _store.selections[key];
                  
                  // 2. Reset initialization flag on the DOM so it can be resurrected later
                  const selector = `[name*="properties[${key.replace(/-/g, '\\-')}]"]`;
                  const inputElement = document.querySelector(selector);
                  const container = inputElement ? inputElement.closest('.bold_option') : null;
                  if (container) {
                      container.classList.remove('bold-facade-init');
                  }
              }
          }
      }
      // --- END PRUNING LOGIC ---


      let sizeSum = 0;
      let optionSum = 0;
      let optionOriginalSum = 0;
      const optionBreakdown = [];
      const sizeBreakdown = [];

      // 1. Matrix Logic
      let matrixTotal = 0;
      let matrixEntriesCount = 0;
      
      const findMatrixData = (key) => {
          if (!_store.matrix) return null;
          const clean = cleanName(key);
          const matchKey = Object.keys(_store.matrix).find(k => cleanName(k) === clean);
          return matchKey ? _store.matrix[matchKey] : null;
      };

      if (_store.matrix) {
         Object.values(_store.matrix).forEach(group => {
             Object.values(group).forEach(q => {
                 if (q > 0) {
                     matrixTotal += q;
                     matrixEntriesCount++;
                 }
             });
         });
      }
      
      _store.isMixedBag = matrixEntriesCount > 1;
      if (_store.isMixedBag || matrixTotal > 0) { _store.quantity = matrixTotal; }

      console.groupCollapsed('[Bold Calc] Recalculating...');
      
      // 2. Quantity Breaks Logic
      let activeTierPrice = _store.originalPrice;
      let activeIndex = -1;
      let nextTierGap = 0;
      let nextTierPrice = 0;

      const qty = _store.quantity;
      
      if (_store.tiers.length > 0) {
          if (qty === 0) {
              activeIndex = -1;
              nextTierGap = 0;
              nextTierPrice = 0;
          } else {
              const match = _store.tiers.find(t => qty >= t.min && qty <= t.max);
              if (match) {
                  activeTierPrice = match.price;
                  activeIndex = match.index;
                  
                  if (activeIndex < _store.tiers.length - 1) {
                      const nextTier = _store.tiers[activeIndex + 1];
                      nextTierGap = nextTier.min - qty;
                      nextTierPrice = nextTier.price;
                  }
              }
          }
      }

      if (_store.currentPrice !== activeTierPrice) { _store.currentPrice = activeTierPrice; }
      _store.activeIndex = activeIndex;
      _store.nextTierGap = nextTierGap;
      _store.nextTierPrice = nextTierPrice;

      // 3. Options Logic
      for (const [key, value] of Object.entries(_store.selections)) {
          const cleanKey = cleanName(key);
          
          if (findMatrixData(key)) continue;

          const def = opts.find(o => cleanName(o.name) === cleanKey);
          
          if (def && def.valuesData) {
             const valuesToCheck = Array.isArray(value) ? value : [value];
             const isSize = cleanKey.includes('size');

             valuesToCheck.forEach(val => {
                 const valStr = cleanValue(val);
                 let match = def.valuesData.find(v => v.value === valStr);
                 if (!match) match = def.valuesData.find(v => cleanValue(v.value) === valStr);
                 if (!match) match = def.valuesData.find(v => valStr.startsWith(cleanValue(v.value)));

                 /*
                 if (match) {
                     let add = parseInt(match.price || 0, 10);
                     
                    if (_store.externalPricing) {
                         const locationKey = _mapOptionToLocation(def.name);
                         if (locationKey) {
                             const count = _extractColorCount(val);
                             add = _calcOptionPrice(locationKey, count);
                             match.price = add; 
                         }
                     }

                     const lineTotal = add * (isSize ? _store.quantity : 1); 
                     
                     const lineItem = { 
                         label: `${def.prettyName}: ${val}`, 
                         qty: isSize ? _store.quantity : 1, 
                         unitPrice: add, 
                         linePrice: lineTotal 
                     };
*/
                if (match) {
                    let add = parseInt(match.price || 0, 10);
                    let spPreviousUnitPrice = null;
                    let spDidReceiveBreak = false;
                                    
                  if (_store.externalPricing) {
                        const locationKey = _mapOptionToLocation(def.name);
                        if (locationKey) {
                            const count = _extractColorCount(val);
                            const pricingMeta = _calcOptionPrice(locationKey, count);
                            add = pricingMeta.unitPrice;
                            match.price = add; 
                            spPreviousUnitPrice = pricingMeta.previousUnitPrice;
                            spDidReceiveBreak = pricingMeta.didReceiveBreak;
                        }
                    }

                    const lineTotal = add * (isSize ? _store.quantity : 1); 
                                    
                    const lineItem = { 
                        label: `${def.prettyName}: ${val}`, 
                        qty: isSize ? _store.quantity : 1, 
                        unitPrice: add, 
                        linePrice: lineTotal,
                        // SP-specific metadata for the breakdown UI
                        spPreviousUnitPrice,
                        spDidReceiveBreak
                    };

                     // UNCONDITIONAL PUSH LOGIC (Show free options too)
                     if (isSize) {
                         sizeBreakdown.push(lineItem);
                         if (add > 0) sizeSum += lineTotal;
                     } 
                     else {
                         optionBreakdown.push(lineItem);
                         if (add > 0) {
                             optionSum += lineTotal;
                         }
                     }
                 }
                
                
             });
          }
      }

      // Compute original (pre-discount) options total for display.
      // For each non-size option, if it has a higher SP previous unit price,
      // treat that as the "original" price; otherwise fall back to the current.
      optionBreakdown.forEach((item) => {
        const currentUnit = item.unitPrice || 0;
        let originalUnit = currentUnit;

        if (item.spPreviousUnitPrice && item.spPreviousUnitPrice > currentUnit) {
          originalUnit = item.spPreviousUnitPrice;
        }

        if (originalUnit > 0) {
          const qty = item.qty || 1; // for options this is effectively 1, but keep it generic
          optionOriginalSum += originalUnit * qty;
        }
      });

      
      // 4. Matrix Logic
      if (_store.matrix) {
          for (const [optName, vals] of Object.entries(_store.matrix)) {
              const def = opts.find(o => cleanName(o.name) === cleanName(optName));
              if (def) {
                  for (const [sizeName, qty] of Object.entries(vals)) {
                      if (qty > 0) {
                          const match = def.valuesData.find(v => cleanValue(v.value) === cleanValue(sizeName));
                          if (match) {
                             const extraUnitCost = match.price || 0;
                             const lineTotal = extraUnitCost * qty;
                             
                             if (extraUnitCost > 0) {
                                 sizeSum += lineTotal; 
                             }
                             
                             sizeBreakdown.push({ 
                                 label: sizeName, 
                                 qty: qty, 
                                 unitPrice: extraUnitCost, 
                                 linePrice: lineTotal
                             });
                          }
                      }
                  }
              }
          }
      }

      console.log(`💰 Sizes: $${sizeSum/100} | Options: $${optionSum/100}`);
      console.groupEnd();

      _store.sizeExtras = sizeSum;
      _store.optionExtras = optionSum;
      _store.optionOriginalExtras = optionOriginalSum; 
      _store.optionBreakdown = optionBreakdown;
      _store.sizeBreakdown = sizeBreakdown;
      _store.extras = sizeSum + (optionSum * _store.quantity);
      
      _store.configuredPrice = _store.currentPrice + _store.optionExtras; 
      _store.totalPrice = _store.configuredPrice + _store.sizeExtras; 
      _store.grandTotal = (_store.currentPrice * _store.quantity) + _store.extras;

      // Only memoize when we have a real variant and price — partial state
      // (e.g. during initialization before Bold writes originalPrice) would
      // poison the cache with a $0 result that gets served on subsequent calls.
      if (_store.variant && _store.originalPrice > 0) {
        _memoCache.set(key, JSON.parse(JSON.stringify(_store)));
      }
      
      api.tools.dispatch('PRICE_RECALCULATED', { grandTotal: _store.grandTotal, totalPrice: _store.totalPrice });
  }

// FILE: assets/bold-sc-adapter.js
api.actions.updateOption = function (key, value, imgSrc = null) {
  const isUnselected =
    value === null ||
    value === '' ||
    value === 'Unchecked' ||
    (Array.isArray(value) && value.length === 0);

  const exactKey = Object.keys(_store.selections).find(k => 
    k.replace(/\*/g, '').trim() === key.replace(/\*/g, '').trim()
  ) || key;

  // --- INSERT DEBOUNCE GATEKEEPER HERE ---
  _store._debounce = _store._debounce || {};
  const now = Date.now();
  if (_store._debounce[exactKey] && (now - _store._debounce[exactKey] < 100)) {
      // If the same key is hit again within 100ms, ignore it.
      return; 
  }
  _store._debounce[exactKey] = now;
  // --- END OF DEBOUNCE ---

  const currentVal = _store.selections[exactKey];
  const isCurrentlySet = currentVal !== undefined;

  // --- THE GATEKEEPER: Prevent double-execution loops ---
  // If we are clearing something already clear, EXIT.
  if (isUnselected && !isCurrentlySet) return;
  // If we are setting something to the exact same value, EXIT.
  if (!isUnselected && JSON.stringify(currentVal) === JSON.stringify(value)) return;

  // FILE DETECTION (Preserved from your original)
  let isFileOption = false;
  try {
    const safeName = CSS.escape(`properties[${key}]`);
    const fileInput = document.querySelector(`input[type="file"][name="${safeName}"]`);
    if (fileInput) isFileOption = true;
  } catch (e) {}

  if (isFileOption) {
    _store.fileUploads = _store.fileUploads || {};
    _store.fileUploads[exactKey] = !isUnselected;
    return;
  }

  // --- LOGIC PATH: CLEAR ---
  if (isUnselected) {
    delete _store.selections[exactKey];
    
    // CRITICAL: Wipe from cache to prevent "Targeted Resurrection" from bringing it back
    if (_store._conditionalValueCache && _store._conditionalValueCache[exactKey]) {
        delete _store._conditionalValueCache[exactKey];
    }

    _recalculateTotals();
    
    api.tools.dispatch('OPTION_SELECTION_CHANGED', {
      field: key,
      value: null,
      image: null 
    });
    
    // console.log(`[Bold Facade] ❌ Cleared: ${exactKey}`);
  } 
  // --- LOGIC PATH: UPDATE ---
  else {
    _store.selections[exactKey] = value;
    
    // Sync cache so resurrection knows the new truth
    if (_store._conditionalValueCache) {
        _store._conditionalValueCache[exactKey] = value;
    }

    _recalculateTotals();

    api.tools.dispatch('OPTION_SELECTION_CHANGED', {
      field: key,
      value: value,
      image: imgSrc 
    });
    
    // console.log(`[Bold Facade] ✅ Selected: ${exactKey} = ${value}`);
  }
};


// # WHY: File options are now completely excluded from selections and
// _conditionalValueCache, so they cannot be accidentally cleared or resurrected
// by the dropdown/checkbox logic. We still keep a simple boolean flag in
// _store.fileUploads[key] (both here and in handleInteraction) so multi-step
// flows can answer “does this field have any file?” without relying on the
// fragile file input UI.




  api.actions.updateQuantity = function (rawVal) {
        const val = parseInt(rawVal, 10);
        if (!isNaN(val) && val !== _store.quantity) {
            _store.quantity = val;
            _recalculateTotals();
            
            api.tools.dispatch('QTY_TOTAL_UPDATED', { 
                newQuantity: val, 
                formattedTotal: api.state.getFormattedGrandTotal() 
            });
            
            _pushLog("CHANGE", "Quantity", val);
        }
  };
  
  api.actions.updateMatrixQuantity = function(optionName, valueName, qty) {
        const val = parseInt(qty, 10) || 0;
        if (!_store.matrix) _store.matrix = {};
        let targetKey = Object.keys(_store.matrix).find(k => cleanName(k) === cleanName(optionName)) || optionName;
        if (!_store.matrix[targetKey]) _store.matrix[targetKey] = {};
        if (_store.matrix[targetKey][valueName] !== val) {
            _store.matrix[targetKey][valueName] = val;
            _recalculateTotals(); 
            
            api.tools.dispatch('QTY_TOTAL_UPDATED', { 
                newQuantity: _store.quantity, 
                formattedTotal: api.state.getFormattedGrandTotal() 
            });

            api.tools.dispatch('MATRIX_QTY_UPDATED', { 
                optionName: optionName, 
                valueName: valueName, 
                quantity: val,
                totalQuantity: _store.quantity
            });
            
            _pushLog('CHANGE', `Matrix: ${valueName}`, val);
        }
  };
  
  api.actions.updateMatrixQuantitySilent = function(optionName, valueName, qty) {
        const val = parseInt(qty, 10) || 0;
        if (!_store.matrix) _store.matrix = {};
        let targetKey = Object.keys(_store.matrix).find(k => cleanName(k) === cleanName(optionName)) || optionName;
        if (!_store.matrix[targetKey]) _store.matrix[targetKey] = {};
        if (_store.matrix[targetKey][valueName] !== val) {
            _store.matrix[targetKey][valueName] = val;
            _pushLog('SILENT_INIT', `Matrix: ${valueName}`, val);
        }
  };
  
  api.actions.recalculateAll = function(force = false) {
        _recalculateTotals(force); // Pass force flag
        _pushLog('ACTION', 'Recalculate All', 'triggered');
  };
  
    api.actions.updateBaseVariant = function(id) {
        const val = parseInt(id, 10);
        if (!isNaN(val) && val !== _store.variant) {
            _store.variant = val;
            _pushLog('CHANGE', 'Base Variant ID', val);

            // Initialize or reset memory status for this variant
            if (!_memoryStatusByVariant[val]) {
                _memoryStatusByVariant[val] = 'unknown';
            }

            // If PRE is available, start a short-lived watcher for qty_breaks
            if (_hasPreApi()) {
                _watchMemoryForVariant(val);
            }

            if (_store.product && _store.product.variants) {
                const variantData = _store.product.variants.find(v => v.id === val);
                if (variantData) {
                    _store.originalPrice = parseInt(variantData.price, 10);
                    _recalculateTotals();
                }
            }
        }
  };


  api.actions.hydrateProduct = function() { 
    try { 
        const script = document.getElementById('bold-hub-data'); 
        if (script) { 
            const data = JSON.parse(script.textContent); 
            _store.product = data; 
            const firstAvailable = data.variants.find(v => v.available) || data.variants[0]; 
            _store.originalPrice = firstAvailable ? parseInt(firstAvailable.price, 10) : parseInt(data.price, 10); 
            if (firstAvailable) _store.variant = firstAvailable.id; 
        } 
        _processExternalPricingJSON(); 
        _recalculateTotals(); 
        api.tools.dispatch('PRODUCT_HYDRATED', { variantId: _store.variant, price: _store.originalPrice }); 
    } catch (e) { console.error(e); } 
  };

function _updateOptionLabelsFromElement(selectEl) {
    if (document.getElementById('sp-pricing-json') === null) return;
    if (selectEl.tagName !== 'SELECT') return;

    Array.from(selectEl.options).forEach(option => {
        const originalText = option.textContent;
        const cleanText = _stripOptionLabelPrice(originalText);
        if (cleanText !== originalText && !originalText.startsWith('--')) {
            option.textContent = cleanText;
        }
    });
}

function handleInteraction(e) { 
  const t = e.target; 
  let label = null;
  
  if (t.classList.contains('matrix-qty-input')) return; 

  if (t.name) { 
    const m = t.name.match(/properties\[(.*?)\]/); 
    if (m) label = m[1]; 
  } 
  
  const parent = t.closest('.bold_option') || t.closest('.product-form__input'); 
  if (!label && parent) { 
    const titleEl = parent.querySelector('.bold_option_title, label, .form__label'); 
    if (titleEl) label = api.helpers.getSafeText(titleEl); 
  } 

  if (t.name === 'quantity' || t.matches('.quantity__input')) { 
    api.actions.updateQuantity(t.value); 
    return; 
  } 

  if (t.name === 'id') { 
    api.actions.updateBaseVariant(t.value); 
    return; 
  } 
  
  const isMatrixControlled = Object.keys(_store.matrix || {}).some(
    k => cleanName(k) === cleanName(label)
  );
  if (isMatrixControlled && !t.classList.contains('matrix-qty-input')) return;
  
  if (!t.matches('.bold_option input, .bold_option select, .bold_option textarea, .product-form__input input')) return;
  if (!label) return; 
  
  // Fix: Capture full text from dropdowns to avoid "inch quote" truncation
  let val = (t.tagName === 'SELECT') ? t.options[t.selectedIndex].text : t.value;
  let capturedImg = null;

  // Handle Swatches (Checkbox/Radio)
  if (t.type === 'checkbox' || t.type === 'radio') { 
    const valueWrapper = t.closest('.bold_option_value'); 
    if (valueWrapper) { 
      // Sync visual selection classes immediately
      if (t.checked) {
        valueWrapper.classList.add('bold_checked', 'bold_swatch_selected'); 
      } else {
        valueWrapper.classList.remove('bold_checked', 'bold_swatch_selected'); 
      }
    } 
    
    if (parent && parent.classList.contains('bold_option_checkboxmulti')) { 
      const allChecked = Array.from(parent.querySelectorAll('input[type="checkbox"]:checked')); 
      val = allChecked.map(c => c.value); 
    } else { 
      val = t.checked ? t.value : 'Unchecked'; 
    } 

    // Capture the image URL for swatches
    const swatchSpan = t.closest('.bold_option_value')?.querySelector('.bold_option_value_swatch span');
    if (swatchSpan && (t.checked || t.tagName === 'SELECT')) {
        capturedImg = swatchSpan.style.backgroundImage.slice(4, -1).replace(/"/g, "");
    }
  }

  else if (t.type === 'file') {
    const hasFiles = t.files && t.files.length > 0;
    if (hasFiles) {
      _store.fileUploads = _store.fileUploads || {};
      _store.fileUploads[label] = true;
    }
    return;
  } 
  
  api.actions.updateOption(label, val, capturedImg); 
}

// # WHY: File inputs now bypass updateOption entirely. We only track a boolean
// “has file for this label” in _store.fileUploads when a real file is chosen,
// and treat Cancel as a no-op. This decouples file UX from the pricing/cache
// system while leaving all other controls unchanged.

  
  document.addEventListener('change', handleInteraction); let inputTimer; document.addEventListener('input', (e) => { clearTimeout(inputTimer); inputTimer = setTimeout(() => handleInteraction(e), 100); });
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', api.actions.hydrateProduct); } else { api.actions.hydrateProduct(); }

  // REPLACED: onChange is simple wrapper for the GENERAL event
  api.options.onChange = function(callback) { 
    document.addEventListener(EVENTS.STATE_CHANGE_GENERAL, (e) => { 
      if (e.detail.action === 'log') { 
          callback({ value: e.detail.record.value, field: e.detail.record.field, formattedTotal: e.detail.formattedTotal }); 
      } 
  }); };
  
  api.options.changeOption = function (nameOrId, value, root) { 
    try { 
        const scope = root || document; 
        const opts = api.options.getOptionSets(scope); 
        const key = String(nameOrId).toLowerCase(); 
        const opt = opts.find((o) => o.id.toLowerCase() === key || o.name.toLowerCase() === key); 
        const human = opt?.prettyName || opt?.name || String(nameOrId); 
        
        const controlSelector = `[name="${CSS.escape(`properties[${human}]`)}"]`;
        const allControls = scope.querySelectorAll(controlSelector);
        
        let control = allControls[0];

        if (!control) {
            const block = Array.from(scope.querySelectorAll(".bold_option")).find(
                (blk) =>
                    (blk.querySelector(".bold_option_title")?.textContent || "").trim().toLowerCase() ===
                    human.toLowerCase()
            );
            control = block?.querySelector("select, input, textarea");
            if (!control) return { ok: false, reason: "control-not-found" };
        }

        const targetValue = value === null ? '' : String(value);
        
        if (control.tagName === "SELECT") {
            control.value = targetValue;
            control.dispatchEvent(new Event("change", { bubbles: true }));
        } else if (opt?.type === "radio" || opt?.type === "dropdown" || control.type === "radio") {
            
            allControls.forEach(input => {
                if (input.checked) {
                    input.checked = false;
                    input.dispatchEvent(new Event("change", { bubbles: true }));
                }
            });

            const targetInput = Array.from(allControls).find(input => input.value === targetValue);
            
            if (targetInput) {
                targetInput.checked = true;
                targetInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
            
        } else if (opt?.type === "checkboxmulti" || control.type === "checkbox") {
            
            const targetValues = Array.isArray(value) ? value.map(String) : [targetValue];
            
            allControls.forEach(input => {
                const shouldBeChecked = targetValues.includes(input.value);
                
                if (input.checked !== shouldBeChecked) {
                    input.checked = shouldBeChecked;
                    input.dispatchEvent(new Event("change", { bubbles: true }));
                }
            });
        
        } else {
            control.value = targetValue;
            control.dispatchEvent(new Event("input", { bubbles: true }));
            control.dispatchEvent(new Event("change", { bubbles: true }));
        }

        return { ok: true };
    } catch (err) {
        console.error('changeOption failed:', err);
        return { ok: false, reason: 'error-during-execution' };
    }
  };
  
  document.addEventListener('hub:bold:variant_changed', () => { api.actions.hydrateProduct(); });

  document.addEventListener('hub:pre:ready', () => {
    const currentVariant = _store.variant;
    if (!currentVariant) return;

    if (!_memoryStatusByVariant[currentVariant]) {
      _memoryStatusByVariant[currentVariant] = 'unknown';
    }

    _watchMemoryForVariant(currentVariant);
  });


  // RESTORED: Conditional Initialization Trigger
  document.addEventListener(EVENTS.PRICE_RECALCULATED, () => {
    // Delay initialization to allow DOM updates/conditional rendering to finish
    setTimeout(() => {
        _initializeOptionsFlow();
        _setupResetObserver();
    }, 50); 
  });
})();